import { useMaterialReactTable } from "material-react-table";
import { useMemo } from "react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import Tooltip from "../../../../components/Common/Tooltip";
import TextField from "../../../../components/Common/TextField";
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import theme from "../../../../theme";

const useCustomTable = (
  data,
  setData,
  handleResChange,
  handleResDelete,
  selectedField
) => {
  const columns = useMemo(() => {
    let baseColumns = [
      {
        accessorKey: "delete",
        header: "Delete",
        size: 50,
        Cell: ({ row }) => (
          <Tooltip title="Delete response" arrow>
            <CloseOutlinedIcon
              sx={{
                cursor: "pointer",
                color: theme.palette.error.main,
                background: theme.palette.error.light,
                borderRadius: "10px",
                padding: "10px",
              }}
              onClick={() => handleResDelete(row.original.id)}
            />
          </Tooltip>
        ),
      },
      {
        accessorKey: "name",
        header: "Response Text*",
        size: 50,
        Cell: ({ row }) => (
          <TextField
            height="small"
            sx={{ width: "150px" }}
            value={row.original.name}
            onChange={(val) => {
              handleResChange(row.original.id, "name", val);
            }}
          />
        ),
      },
      {
        accessorKey: "titlePosition",
        header: "Response Text Placement",
        size: 50,
        Cell: ({ row }) => (
          <FormControl>
            <Select
              size="small"
              name="namePosition"
              value={row.original.titlePosition}
              onChange={(e) =>
                handleResChange(
                  row.original.id,
                  "titlePosition",
                  e?.target?.value
                )
              }
            >
              <MenuItem value="Above">Above</MenuItem>
              <MenuItem value="Below">Below</MenuItem>
              <MenuItem value="Left">Left</MenuItem>
              <MenuItem value="Right">Right</MenuItem>
            </Select>
          </FormControl>
        ),
      },
      {
        accessorKey: "allowableValue",
        header: "Stored Value",
        size: 50,
        Cell: ({ row }) => (
          <TextField
            height="small"
            sx={{ width: "150px" }}
            value={row.original.allowableValue}
            onChange={(val) => {
              handleResChange(row.original.id, "allowableValue", val);
            }}
          />
        ),
      },
      {
        accessorKey: "exclusiveAnswer",
        header: "Exclusive Answer",
        size: 50,
        Cell: ({ row }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={row.original.exclusiveAnswer === true}
                onChange={(e) => {
                  handleResChange(
                    row.original.id,
                    "exclusiveAnswer",
                    e?.target?.checked
                  );
                }}
              />
            }
          />
        ),
      },
      {
        accessorKey: "includeCom",
        header: "Include Comment Field",
        size: 50,
        Cell: ({ row }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={row.original.includeCom === true}
                onChange={(e) => {
                  handleResChange(
                    row.original.id,
                    "includeCom",
                    e?.target?.checked
                  );
                }}
              />
            }
          />
        ),
      },
      {
        accessorKey: "comFieldHeading",
        header: "Comment Field Heading",
        size: 50,
        Cell: ({ row }) => (
          <TextField
            disabled={row.original.includeCom === false}
            height="small"
            sx={{ width: "150px" }}
            value={row.original.comFieldHeading}
            onChange={(val) => {
              handleResChange(row.original.id, "comFieldHeading", val);
            }}
          />
        ),
      },
      {
        accessorKey: "comFieldPlace",
        header: "Comment Field Placement",
        size: 50,
        Cell: ({ row }) => (
          <FormControl>
            <Select
              disabled={row.original.includeCom === false}
              size="small"
              name="namePosition"
              value={row.original.comFieldPlace}
              onChange={(e) =>
                handleResChange(
                  row.original.id,
                  "comFieldPlace",
                  e?.target?.value
                )
              }
            >
              <MenuItem value="Above">Above</MenuItem>
              <MenuItem value="Below">Below</MenuItem>
              <MenuItem value="Left">Left</MenuItem>
              <MenuItem value="Right">Right</MenuItem>
            </Select>
          </FormControl>
        ),
      },
      {
        accessorKey: "comFieldLabel",
        header: "Comment User Label",
        size: 50,
        Cell: ({ row }) => (
          <TextField
            disabled={row.original.includeCom === false}
            height="small"
            sx={{ width: "150px" }}
            value={row.original.comFieldLabel}
            onChange={(val) => {
              handleResChange(row.original.id, "comFieldLabel", val);
            }}
          />
        ),
      },
      {
        accessorKey: "newColumn",
        header: "New Column",
        size: 50,
        Cell: ({ row }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={row.original.newColumn === true}
                onChange={(e) => {
                  handleResChange(
                    row.original.id,
                    "newColumn",
                    e?.target?.checked
                  );
                }}
              />
            }
          />
        ),
      },
    ];

    if (selectedField === "Radio Field" || selectedField === "Combobox Field") {
      baseColumns = baseColumns.filter(
        (column) => column.accessorKey !== "exclusiveAnswer"
      );
    }

    if (selectedField !== "Radio Field" && selectedField !== "CheckBox Field") {
      baseColumns = baseColumns.filter(
        (column) => column.accessorKey !== "newColumn"
      );
    }

    if (selectedField === "Combobox Field") {
      baseColumns = baseColumns.filter(
        (column) =>
          column.accessorKey !== "includeCom" &&
          column.accessorKey !== "comFieldHeading" &&
          column.accessorKey !== "comFieldPlace" &&
          column.accessorKey !== "comFieldLabel"
      );
    }

    return baseColumns;
  }, [handleResChange, handleResDelete, selectedField]);

  return useMaterialReactTable({
    columns,
    data,
    enableRowOrdering: true,
    enableSorting: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    initialState: { density: "compact" },
    enablePagination: false,
    muiTableContainerProps: {
      sx: {
        maxHeight: "500px",
        overflowY: "auto",
      },
    },
    muiTableHeadProps: {
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
      },
    },
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = table.getState();
        if (hoveredRow && draggingRow) {
          data.splice(
            hoveredRow.index,
            0,
            data.splice(draggingRow.index, 1)[0]
          );

          if (draggingRow.index !== hoveredRow.index) {
            const updatedData = data.map((item, index) => ({
              ...item,
              sequence: index,
            }));

            setData(updatedData);
          }
        }
      },
    }),
  });
};

export default useCustomTable;
